// SinaPlot.js

import * as d3 from 'd3';
import _ from 'lodash';
import Labeler from './Labeler.js';
import helpers from './util/helpers';

// Color scheme for the Sina Plot
const colors = {
  blue: "#097dc2",
  darkblue: "00659d",
  orange: "#ff5000",
  white: 'white',
}

// functions for a random number with a seed
const randomsfc32 = function(a, b, c, d) {
    return function() {
      a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0;
      var t = (a + b) | 0;
      a = b ^ b >>> 9;
      b = c + (c << 3) | 0;
      c = (c << 21 | c >>> 11);
      d = d + 1 | 0;
      t = t + d | 0;
      c = c + t | 0;
      return (t >>> 0) / 4294967296;
    }
}

// Helper for building translation strings for use in SVG transform attributes
const translate = function(x, y) { return "translate(" + x + "," + y + ")"; };
const jitter = function(width) { return Math.floor(Math.random() * width) - width / 2; };

// Standard deviation of an array
const sd = function(xs) {
  const mean = _.mean(xs);

  let diff = 0;
  for(let i = 0; i < xs.length; i++) { diff += Math.pow(xs[i] - mean, 2); }

  return Math.sqrt(diff / (xs.length - 1));
}

var makeLinearInterpolator = function(domain, range) {
  var values = _.sortBy(_.zip(domain, range), function(x) { return x[0]; });
  return function(x) {
    if(x <= domain[0]) return range[0];
    if(x >= domain[domain.length - 1]) return range[range.length - 1];

    for(var i = 0; i < values.length - 1; i++) {
      if(x >= values[i][0] && x <= values[i + 1][0]) {
        return values[i][1] + (x - values[i][0]) * (values[i + 1][1] - values[i][1]) / (values[i + 1][0] - values[i][0]);
      }
    }
  };
}

const roundToLog10 = function(x) {
  return Math.pow(10, Math.floor(Math.log10(x)));
}

const integer = function(x) {
  return Math.floor(x) === x;
}

const snapExtentToLog10 = function(extent) {
  const lower = roundToLog10(extent[0]);
  const upper = integer(Math.log10(extent[1])) ? extent[1] : 10 * roundToLog10(extent[1]);
  return [lower, upper];
}

function kernelDensityEstimator(kernel, x) {
  return function (sample) {
    return x.map(function (x) {
      let k = sample;
      for(let i = 0; i < k.length; i++) k[i] = kernel(x - sample[i]);
      return { x: x, y: _.mean(k) };
    });
  };
}


function kernelDensityEstimatorGaussian(scale, x) {
  const c = 1 / (Math.sqrt(2 * Math.PI));
  return function (sample) {
    let ret = [];
    for(let i = 0; i < x.length; i++) {
      let sum = 0;
      for(let j = 0; j < sample.length; j++) {
        const u = (x[i] - sample[j]) / scale;
        sum += c * Math.exp(-0.5 * u * u);
      }
      ret.push({ x: x[i], y: sum / (sample.length * scale)});
    }

    return ret;
  };
}

function gaussianKernel(scale) {
  var c = 1 / (Math.sqrt(2 * Math.PI));
  return function(u) {
    u /= scale;
    return c * Math.exp(-0.5 * u * u);
  }
}

/*
 * SinaPlot
 *
 * The core idea of a SinaPlot as a data visualization is that it is a dot plot paired with a violin plot.
 *
 * In other words, each participant's measurement is plotted on to the chart as a dot with its y
 * position determined by the measurement level (like a dot plot), but its x position is allowed
 * to vary in relationship to the density of points near it.
 *
 * The effect is that the viewer can see where the highest density of measurements is
 * by looking at the width of the point cloud.
 *
 * Mechanically, you need to choose a kernel density estimator and set its parameters.
 * This implementation currently uses a gaussian density estimator.
 *
 * The density is sampled at a number of points, with any holes filled in by linear interpolation.
 *
 *
 * This implementation renders the visualization to both a canvas and SVG. The canvas is used
 * to plot the measurements of the other participants (1,000+ points, which would bog down the browser
 * if done in SVG). All other elements (graph labels, axes, current participant measurements) are
 * rendered with SVG.
 *
 */
class SinaPlot {
  constructor(container, avatar) {
    this.container = container;

    // Plot margins
    this.margin = { top: 0, right: 10, bottom: 0, left:10 };

    // Width and height of the participant's measurement
    this.faceSize = 40

    this.avatar = avatar;

    // Plot dimensions
    this.width = document.documentElement.clientWidth;
    this.width = Math.min(this.width, 650)
    this.height = 500 - this.margin.top  - this.margin.bottom;
    this.nonDetectWidth = 80;
    this.nonDetectChartArea = this.nonDetectWidth - 10;
    this.detectWidth = this.width - this.margin.right - this.margin.left - this.nonDetectWidth
    this.nonDetectCenter = 48;
    this.labelOffset = 35;
    this.axisHeight = 20;
    this.graphMidline = 50
    this.participantFaceRadius = 10
    this.participantSmallCircleRadius = 8;
    this.benchmarkLineThickness = 4

    // Resolution of the kernel density estimate
    // in other words, the kernel density will be estimated
    // at this many points.
    this.resolution = 512;

    this.kdeArea = d3.area()
      .x0(0)
      .x1((d) => d.x)
      .y((d) => this.y(d.y))
      .curve(d3.curveCatmullRom.alpha(0.5));

    this.expanded = false;
  }

  clear() {
    d3.select(this.container).text('')
  }

  /*
   * Initializes the container for the graph. Sets up scales and axes.
   */
  setup() {
    this.graphContainer = d3.select(this.container)
      .append('div')
        .attr('class', 'graph-container');

    // let width = this.width - this.margin.left - this.margin.right;
    let height;
    if (this.expanded) {
      height = 280 - this.margin.top  - this.margin.bottom;
      this.axisHeight = 45;
      this.graphMidline = 170;
      this.labelOffset = 110;
    } else {
      height = 100 - this.margin.top  - this.margin.bottom;
      this.axisHeight = 20;
      this.graphMidline = 50
      this.labelOffset = 35;

    }
    this.height = height
    let width = this.width


    this.canvas = this.graphContainer
      .append('canvas')
        .attr('width', width) /* this.width + this.margin.left + this.margin.right) */
        .attr('height', height);

    this.chart = this.graphContainer
      .append('svg')
        .attr('width', width) /* this.width + this.margin.left + this.margin.right) */
        .attr('height', height)
      .append('g')
        // .attr('transform', translate(this.margin.left, this.margin.top));

    this.x = d3.scaleOrdinal().range([0, height-this.axisHeight]);
    this.y = d3.scaleLog().range([this.nonDetectWidth + this.margin.left, width - this.margin.right]);

    this.xAxis = d3.axisLeft()
      .scale(this.x);

    this.yAxis = d3.axisBottom()
      .scale(this.y)
      .tickFormat(d3.format(',.1r'));

    // Containers for holding the x and y axes and axis labels
    this.chart.append('g')
      // .attr('transform', translate(30, 0))
      .attr('class', 'y axis');

    this.chart.append('g')
      .attr('class', 'x axis');
  } // end setup()

  /*
   * Plots measurement data and benchmark levels.
   *
   * TODO: Refactor into multiple smaller methods.
   */
  updateData(data, benchmarks, participantId) {
    let that = this;
    this.data = data;

    this.participantData = _.filter(data, { mine: true });

    this.detectedData = _.filter(data, (d) => d.detected === true && !d.mine);
    this.nondetectData  = _.filter(data, (d) => d.detected === false && !d.mine);

    const xUniqValues = _(data).map('chemical').uniq().value();

    let barWidth = this.height / xUniqValues.length;
    if (this.expanded) {barWidth = barWidth * 0.7}
    let kdeMaxWidth = barWidth;
    // we want to make the graphs closer to a line when there are very few points
    // so make the max width smaller based on the number of detects.
    let scaleThreshold = 100
    let kdeMinWidth = 4
    let numDetects = this.detectedData.length
    if (numDetects < scaleThreshold) {
      let slope = (kdeMaxWidth-kdeMinWidth)/scaleThreshold
      kdeMaxWidth = slope*numDetects + kdeMinWidth
      // kdeMaxWidth = (kdeMaxWidth-kdeMinWidth)/(1+Math.pow(Math.E, -(numDetects-scaleThreshold))) + kdeMinWidth
    }

    this.x.domain(xUniqValues)

    if(xUniqValues.length === 1) {
      this.x.range([this.height]);
    }
    else {
      this.x.range(_.range(0, this.height, barWidth));
    }

    this.y.domain(this.yLimits).nice()

    let formattedLabels = [0.0001, 0.001, 0.01, 0.1, 1, 10, 100, 1000, 10000, 100000]
    let numTicks;
    if (this.expanded) {
      let ticksInRange = formattedLabels.filter((d) => d >= this.y.domain()[0] && d <= this.y.domain()[1])
      this.yAxis.tickValues(ticksInRange)
      numTicks = ticksInRange.length
    } else {
      numTicks = 2;
      let minDomain = this.y.domain()[0]
      let maxDomain = this.y.domain()[1]

      let minLabel;
      let maxLabel;
      for (let i=0; i<formattedLabels.length; i++) {
        let label = formattedLabels[i]
        if (label <= minDomain) {minLabel = label}
      }
      for (let i=formattedLabels.length-1; i>=0; i--) {
        let label = formattedLabels[i]
        if (label >= maxDomain) {maxLabel = label}
      }
      this.yAxis.tickValues([minLabel, maxLabel])
    }

    let formatComma = d3.format(",.1r")
    this.yAxis.tickFormat(function(d, i){
      if (i == 0) {
        return "" + formatComma(d) + that.units
      } else {
        return formatComma(d)
      }
    });

    if (this.expanded) {
      this.chart
        .select('.y.axis')
        .call(this.yAxis)
        .attr('transform', `translate(0,${this.height - this.axisHeight})`)
    } else {
      this.chart
        .select('.y.axis')
        .call(this.yAxis.ticks(2))
        .attr('transform', `translate(0,${this.height - this.axisHeight})`)
    }

    this.chart
      .select('.y.axis')
      .selectAll("text")
      .attr('font-size', 12)
      .attr("text-anchor", function(d,i) {
        if (i == 0) {return 'start'}
        else if (i == numTicks-1) {return 'end'}
        else {return 'middle'}
      })
      .style("font-weight", function(d,i) {
        if (i == 0 || i == numTicks-1) {return 'bold'}
        else {return null}
      })

    if (this.expanded) {
      this.chart
        .select('.y.axis')
        .selectAll("text")
        .attr("transform", function(d,i) {
          if (i == 0 || i == numTicks-1) {
            return 'translate(0,14)'
          }
          else {return null}
        });

      this.chart
        .select('.y.axis')
        .selectAll("line")
        .attr("y1", function(d,i) {
          if (i == 0 || i == numTicks-1) {
            return 0
          }
          else {return null}
        })
        .attr("y2", function(d,i) {
          if (i == 0 || i == numTicks-1) {
            return 18
          }
          else {return 6}
        });
    }

    this.displayNames = []
    data.forEach((d) => this.displayNames[d.chemical] = d.display_name)

    //
    // X Axis setup
    //
    this.xAxis.tickFormat((d) =>
      this.displayNames[d] === undefined ? d : this.displayNames[d]
    )


    // Set up the title of the graph
    // if(this.titleClickable == true) {
      // xAxisSelection.select('text')
      //   .style('fill', 'white')

      // xAxisSelection.select('.tick')
      //   .insert('rect', 'text')
      //     .attr('x', function(d) {
      //       return -1 * (this.parentNode.getBBox().width + 20) / 2.0;
      //     })
      //     .attr('y', function(d) {
      //       return -1 * (this.parentNode.getBBox().height + 20) + 13;
      //     })
      //     .attr('width', function(d) {
      //       return this.parentNode.getBBox().width + 20;
      //     })
      //     .attr('height', function(d) {
      //       return this.parentNode.getBBox().height + 6;
      //     })
      //     .style('fill', colors.blue)
      //     .style('stroke', colors.darkblue);

      // this.chart.select('.x.axis')
      //   .on('click', () => this.onChemicalClicked(xUniqValues[0]));
    // }

    // Remove x axis line
    this.chart.selectAll('.x.axis .domain, .x.axis line').remove();

    var kdeData = _(xUniqValues)
      .mapKeys(function(value, key) {
        return value;
      })
      .mapValues(function(x, key) {
        var values = _(that.detectedData).map('value').map(Math.log10).value();

        //var bandwidth = 1.06 * sd(_.map(values, Math.log10)) * Math.pow(values.length, -1.0/5.0)
        //
        // In R this is bw.nrd function
        var bandwidth = 0.9 * sd(values) * Math.pow(values.length, -0.2);
        // if there is just one point, the standard deviation is NaN, so just set the bandwith to 1
        if (values.length == 1) {
          bandwidth = 1
        }

        var minValue = d3.min(values);
        var maxValue = d3.max(values);

        const ticks = Array(that.resolution).fill().map((_, i) => minValue + i * (maxValue - minValue) / that.resolution);
        var kde = kernelDensityEstimatorGaussian(bandwidth, ticks);

        var kdeData = kde(values);
        var kdeScale = d3.scaleLinear()
          .domain(d3.extent(_.map(kdeData, 'y')))
          .range([0, kdeMaxWidth]);

        var kdeWidths = _.map(kdeData, function(d) {
          return { x: kdeScale(d.y), y: Math.pow(10, d.x) };
        });

        var linearInterpolator = makeLinearInterpolator(_.map(kdeWidths, 'y'), _.map(kdeWidths, 'x'));

        var kdeWidth = function(y) {
          return linearInterpolator(y);
        }

        return {
          kdeData: kdeData,
          kdeScale: kdeScale,
          kdeWidths: kdeWidths,
          kdeWidth: kdeWidth
        };
      })
      .value();  // end --> var kdeData =

    //
    // Draw other participants (the blue dots) onto a canvas.
    // This uses canvas because it is much more performant than
    // drawing 1,000+ circles as SVG elements.
    //
    if(this.canvas.node() === null) return;
    const context = this.canvas.node().getContext('2d');

    // for(let i = 0; i < 10; i++) {
    // let widths = []

    let randomSeeded = randomsfc32(2,3,4,5)

    for(let i = 0; i < this.detectedData.length; i++) {
      let d = this.detectedData[i];

      let x = this.graphMidline;

      const y = Math.round(that.y(d.value));
      let show = true
      if(that.sina) {
        const kdeWidth = kdeData[d.chemical].kdeWidth(d.value)/4;
        x += Math.round(((randomSeeded() * kdeWidth*2) - kdeWidth) * that.jitterAmount);
        // x += Math.round(jitter(kdeWidth * 2) * that.jitterAmount);

        // Probabalistic sampling
        // let chance = 3 / kdeWidth;
        // let rand = Math.random()
        // if (rand < chance) {
        //   show = true;
        // } else {
        //   show = false;
        // }

        // only sample compressed graphs
        // if (!this.expanded){
        //   // sample all equally except outlyers
        //   let threshold = 2;
        //   let chance = 0.25;
        //   let rand = Math.random()
        //   if (kdeWidth < threshold || rand < chance) {
        //     show = true;
        //   } else {
        //     show = false;
        //   }
        // }

        // sample all equally
        // let chance = 0.25;
        // let rand = Math.random()
        // if (rand < chance) {
        //   show = true;
        // } else {
        //   show = false;
        // }

      }
      else if(that.jitter) {
        x += Math.round(jitter(that.height / 3 * that.jitterAmount));
      }


      if (show) {
        context.beginPath();
        let radius = 4;
        context.arc(y, x, radius, 0, 2 * Math.PI, false);
        context.strokeStyle = "rgba(119, 158, 255,1)"
        context.lineWidth = 1;
        context.stroke();
      }
    }


    //
    // Add participant's detected data points
    //
    if (this.expanded) {
      if(this.detectedData.length > 0) {
        this.chart.selectAll('.otherParticipantsClickTarget')
          .data([this.detectedData[0].chemical])
          .enter()
          .append('rect')
          .attr('class', 'otherParticipantsClickTarget')
          .attr('transform', (d) => translate(this.nonDetectWidth, this.graphMidline-this.labelOffset + 50))
          .attr('width', this.width - this.nonDetectWidth)
          .attr('height', this.labelOffset*2 - 100)
          .style('fill', 'transparent')
          .on('click', (d) => this.onOtherParticipantsClicked ? this.onOtherParticipantsClicked(d) : null);

        this.chart.selectAll('.axisClickTarget')
          .data([this.detectedData[0].chemical])
          .enter()
          .append('rect')
          .attr('class', 'axisClickTarget')
          .attr('transform', (d) => translate(-25, 0))
          .attr('width', 55)
          .attr('height', this.height)
          .style('fill', 'transparent');
      }
  }

    if (this.expanded) {
      if(this.nondetectData.length > 0) {
        this.chart.selectAll('.nondetectClickTarget')
        .data([this.nondetectData[0].chemical])
        .enter()
        .append('rect')
        .attr('class', 'nondetectClickTarget')
        .attr('transform', (d) => translate(this.margin.left, this.graphMidline-this.labelOffset-50))
        .attr('width', this.nonDetectWidth)
        .attr('height', this.labelOffset*2)
        .style('fill', 'transparent')
        .on('click', (d) => this.onNonDetectClicked ? this.onNonDetectClicked(d) : null);
      }
    }

    //
    // Non-detects
    //

    // let kdeWidthND = this.nondetectData.length/10; // NEW WAY: make the ND width consistent across chemicals since based on actual NDs

    // the non-detects should fill a circle proportionally to the amount of nondetects,
    // with a jitter
    // scale proportionately to area, not radius
    // let dataLength = this.detectedData.length + this.nondetectData.length;
    // let dotRadius = 4;
    // let minNonDetectArea = Math.PI * Math.pow(dotRadius + 2, 2) // pi*r^2
    // let maxNonDetectArea = Math.PI * Math.pow(this.nonDetectWidth, 2)
    // let nonDetectArea = this.map(this.nondetectData.length, 0, dataLength, minNonDetectArea, maxNonDetectArea)
    // let nonDetectRadius = Math.sqrt(nonDetectArea/Math.PI) // r = sqrt(A/pi)

    // the non-detects should fill a rectagle proportionally to the amount of nondetects,
    // with a jitter
    // scale proportionately to area, not radius
    let dataLength = this.detectedData.length + this.nondetectData.length;
    let proportionNonDetect = this.nondetectData.length / dataLength;
    let sideLengthScale = Math.sqrt(proportionNonDetect)

    let dotRadius = 4;
    // let minNonDetectArea = Math.PI * Math.pow(dotRadius + 2, 2) // pi*r^2
    // let maxNonDetectArea = Math.PI * Math.pow(this.nonDetectWidth, 2)
    // let nonDetectArea = this.map(this.nondetectData.length, 0, dataLength, minNonDetectArea, maxNonDetectArea)
    // let nonDetectRadius = Math.sqrt(nonDetectArea/Math.PI) // r = sqrt(A/pi)


    let numNonDetects = this.nondetectData.length;

    // scale width so it doesn't look weird with just a few dots;
    let width = this.nonDetectChartArea*sideLengthScale
    if (numNonDetects < 10) {width = dotRadius + dotRadius*0.5*numNonDetects}

    let maxNonDetectHeight = (that.height-2*that.axisHeight-that.margin.top-that.margin.bottom)/2
    if (that.expanded) {
      maxNonDetectHeight -= 45
    } else {
      maxNonDetectHeight -= 10
    }
    let heightND = maxNonDetectHeight * sideLengthScale
    if (heightND > maxNonDetectHeight) {heightND = maxNonDetectHeight}


    let randomSeededNonDetect = randomsfc32(2,3,4,5)

    this.nondetectData.forEach(d => {
      // let kdeWidth;
      // if (helpers.findGetParameter('old'))
      //   kdeWidth = kdeData[d.chemical].kdeWidth(d.value) || 60; // OLD WAY: not right... based on *detected* data and a ND value
      // else
      //   kdeWidth = kdeWidthND;


      let randomXJitter = randomSeededNonDetect()

      // keep a random sample away from the edges
      let thousandsPlace =  Math.ceil(randomXJitter*100)*10 - Math.floor(randomXJitter*1000)
      let randomSample1 = thousandsPlace < 3
      let randomSample2 = thousandsPlace < 5
      let customScale = 1;
      let customScaleOffset = 0
      if (!randomSample1) {customScale = 0.8; customScaleOffset = 0.1}
      if (randomSample2) {customScale = 0.9; customScaleOffset = 0.05}



      let x = that.nonDetectWidth - (randomXJitter * width * customScale) - width*customScaleOffset

      // x = randomSeededNonDetect() *width
      // x = that.nonDetectWidth - width

      // Scale the y-coordinate slightly to make rounded corners
      // let radius = 0.1; // in percents
      // let radialScale = 1;
      // let randomSample = Math.ceil(randomXJitter*100)*10 - Math.floor(randomXJitter*1000) == 2
      // if (randomXJitter < radius && randomSample) {
      //   let triangleX = radius - randomXJitter;
      //   let triangleY = Math.sqrt(Math.pow(radius, 2) - Math.pow(triangleX, 2))
      //   let yScalePercent = (1-2*radius) + (2*triangleY)
      //   radialScale = yScalePercent
      // }
      // if (randomXJitter > (1-radius) && randomSample) {
      //   let triangleX = randomXJitter - (1-radius);
      //   let triangleY = Math.sqrt(Math.pow(radius, 2) - Math.pow(triangleX, 2))
      //   let yScalePercent = (1-2*radius) + (2*triangleY)
      //   radialScale = yScalePercent
      // }



      let y = that.graphMidline + jitter(heightND * 2) * that.jitterAmount * customScale;

      context.beginPath();
      context.arc(x, y, 4, 0, 2 * Math.PI, false);
      context.strokeStyle = "#ccc";
      context.lineWidth = 1;
      context.stroke();
    });

    let nonDetectFontsize = 12
    const nondetectLabel = this.chart.selectAll('.nondetect-label')
      .data([1])
      .enter()
      .append('g')
        .attr('transform', () => translate(5, this.height - this.axisHeight + nonDetectFontsize/2));

    const [ndt1, ndt2] = this.noDetectText.split(" "); // no easy way to put it on 2 lines

    nondetectLabel
      .append('text')
        .attr('text-anchor', 'start')
        .attr('class', 'nondetect-label')
        .style('font-size', `${nonDetectFontsize}px`)
        .style('font-weight', 'bold')
        .attr('y', 5)
        .text(this.noDetectText);

    // nondetectLabel
    //   .append('text')
    //     .attr('text-anchor', 'start')
    //     .attr('class', 'nondetect-label')
    //     .style('font-size', `${nonDetectFontsize}px`)
    //     .text(ndt1);
    //
    // nondetectLabel
    //   .append('text')
    //   .attr('y', nonDetectFontsize)
    //   .attr('text-anchor', 'start')
    //   .attr('class', 'nondetect-label')
    //   .style('font-size', `${nonDetectFontsize}px`)
    //   .text(ndt2);

    //
    // Non detect bubble
    //
    if (this.expanded) {
      let numNonDetects = this.nondetectData.length;
      let numDetects = this.detectedData.length
      let nonDetectPercent = numNonDetects / (numNonDetects+numDetects) * 100
      let nonDetectText;
      if (nonDetectPercent == 0) {nonDetectText = "0%"}
      else if (nonDetectPercent < 1) {nonDetectText = "<1%"}
      else if (nonDetectPercent > 99 && nonDetectPercent < 100) {nonDetectText = "99%"}
      else {nonDetectText = Math.round(nonDetectPercent)+"%"}

      let nondetectBubble = this.chart.selectAll('.nondetect-bubble')
        .data([0])
        .enter()
        .append('g')
          .attr('transform', () => translate(this.nonDetectCenter-this.participantSmallCircleRadius/2, this.graphMidline));

        // nondetectBubble.append('line')
        //   .attr("x1", 0)
        //   .attr("y1", 0)
        //   .attr("x2", 0)
        //   .attr("y2", -this.labelOffset)
        //  .style('stroke', '#aaa')
        //  .style('stroke-width', '2')
        //  .attr('transform', () => translate(0, 0));
      let nonDetectBubbleY = -heightND-2*this.participantFaceRadius-10

      nondetectBubble.append('circle')
       .attr('r', 2*(this.participantFaceRadius))
       .style('fill', 'white')
       .style('stroke', '#aaa')
       .style('stroke-width', '2')
       .attr('transform', () => translate(0, nonDetectBubbleY));

     nondetectBubble.append('text')
      .text(nonDetectText)
      .style('fill', '#aaa')
      .style('text-anchor', 'middle')
      .style('dominant-baseline','central')
      .style('font-weight', 'bold')
      .attr('transform', () => translate(0, nonDetectBubbleY));

    // nondetectBubble.append('text')
    //  .text(ndt1)
    //  .style('fill', '#aaa')
    //  .style('text-anchor', 'middle')
    //  .style('dominant-baseline','central')
    //  .style('font-weight', 'bold')
    //  .style('font-size', 14)
    //  .attr('transform', () => translate(0, nonDetectBubbleY-this.participantFaceRadius-35));

   nondetectBubble.append('text')
    .text(this.noDetectText)
    .style('fill', '#aaa')
    .style('text-anchor', 'middle')
    .style('dominant-baseline','central')
    .style('font-weight', 'bold')
    .style('font-size', 14)
    .attr('transform', () => translate(3, nonDetectBubbleY-this.participantFaceRadius-20));



      nondetectBubble.on('click', (d) => this.onNonDetectClicked ? this.onNonDetectClicked(d) : null);
    }

    //
    // Find positions for participant and benchmark labels
    //

    let participantDetected = true;
    for (let i = 0; i < this.participantData.length; i++) {
      if (this.participantData[i].detected == false) {
        participantDetected = false;
        break;
      }
    }

    let benchmarkDetected = true
    for (let i = 0; i < benchmarks.length; i++) {
      if (benchmarks[i].detected == false) {
        benchmarkDetected = false;
        break;
      }
    }

    let detectZeroPoint = this.margin.left + this.nonDetectWidth
    const labeler = new Labeler();
    const labels = _.map(this.participantData, function(d) {
      const x =  0;// doesnt matter, set later that.y(d.chemical);
      let y;
      if (!d.detected) {
        y = 0
      } else {
        y = that.y(d.value) - detectZeroPoint;

        if (y<=0) {y = 1- (1/y)}
      }
      // let y = d.detected ? that.y(d.value) : detectZeroPoint;
      // y-=that.detectWidth
      // if (y < 0) {y = 0}
      return {
        x,
        y,
        width: that.faceSize + 20,
        height: that.faceSize + 20,
        r: (that.faceSize + 10) / 2
      }
    })
    // .concat(_.map(benchmarks, function(d) {
    //   const x =  0;// doesnt matter, set later that.y(d.chemical);
    //   const y = d.detected ? that.y(d.value) : that.nonDetectWidth/2 + that.margin.left;
    //   return {
    //     x: x,
    //     y: y,
    //     width: that.faceSize + 10,
    //     height: that.faceSize + 10,
    //     r: (that.faceSize + 10) / 2
    //   }
    // }));

    const anchors = _.map(labels, _.clone);

    labeler
      .label(labels)
      .anchor(anchors)
      // .width(this.width)
      // .height(this.height + 75)
      // .width(this.height)
      .width(this.height)
      .height(this.width - this.margin.right - this.margin.left - this.nonDetectWidth)
      .start(1000);

    //
    // Benchmarks
    //

    const benchmark = this.chart.selectAll('.benchmark')
      .data(benchmarks)
      .enter()
      .append('g')
        .attr('transform', (d) => translate(d.detected ? this.y(d.value) : (that.nonDetectCenter - this.benchmarkLineThickness/2), this.graphMidline));

    let benchmarkHeight = this.labelOffset
    if (this.expanded) {benchmarkHeight -= 50}

    let benchmarkBottomYOffset = -5;
    if (this.expanded) {benchmarkBottomYOffset = 0}

    if (benchmarkDetected && !(this.tutorialMode && this.tutorialSettings.noBenchmark)) {
      benchmark.append('line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', -benchmarkHeight+2)
        .attr('y2', benchmarkHeight + benchmarkBottomYOffset-2)
        .style('stroke', "rgb(13 94 143)")
        .style('stroke-width', this.benchmarkLineThickness)
        .style('stroke-linecap', 'round')
    }



    //
    // Participant icon
    //

    let expandedOffset = 0
    const participant = this.chart.selectAll('.participant')
      .data(this.participantData)
      .enter()
      .append('g')
        .attr('class', 'participant')
        .attr('transform', function(d) {
          // const y = that.x(d.chemical);
          let y = that.graphMidline;
          let x
          if (d.detected) {
            x = Math.round(that.y(d.value))
            if (x < (that.nonDetectWidth + that.margin.left + 15)) {expandedOffset = 15}
          } else {
            x = that.nonDetectCenter - that.participantSmallCircleRadius/2
          }
          return translate(x, y);
        });

    if (this.expanded && !(this.tutorialMode && this.tutorialSettings.noAvatar)) {

      participant.append('line')
        .attr('x1', 0)
        .attr('x2',function(d, i) {
          let x
          if (d.detected) {
            x = labeler.lab[i].y+detectZeroPoint - that.y(d.value)
            if (that.expanded) {
              x = x + expandedOffset
            }
          } else {
            x = labeler.lab[i].y+that.participantFaceRadius + that.nonDetectWidth/2 + that.participantSmallCircleRadius/2+18
          }
          return x
        })
        .attr('y1', 0)
        .attr('y2',  -this.labelOffset)
        .style('stroke-width', 6)
        .style('stroke', 'white');

      participant.append('line')
        .attr('x1', 0)
        .attr('x2',function(d, i) {
          let x
          if (d.detected) {
            x = labeler.lab[i].y+detectZeroPoint - that.y(d.value)
            if (that.expanded) {
              x = x + expandedOffset
            }
          } else {
            x = labeler.lab[i].y+that.participantFaceRadius + that.nonDetectWidth/2 + that.participantSmallCircleRadius/2+18
          }
          return x
        })
        .attr('y1', 0)
        .attr('y2', -this.labelOffset)
        .style('stroke-width', 2)
        .style('stroke', colors.orange);

    // participant.append('line')
    //   .attr('x1', 0)
    //   .attr('x2', this.labelOffset)
    //   .attr('y1', 0)
    //   .attr('y2', (d, i) => labeler.lab[i].y - (d.detected ? that.y(d.value) : that.height + 15) )
    //   .style('stroke-width', 6)
    //   .style('stroke', 'white');
    //
    // participant.append('line')
    //   .attr('x1', 0)
    //   .attr('x2', this.labelOffset)
    //   .attr('y1', 0)
    //   .attr('y2', (d, i) => labeler.lab[i].y - (d.detected ? that.y(d.value) : that.height + 15) )
    //   .style('stroke-width', 2)
    //   .style('stroke', colors.orange);
    }

    let participantIcon
    if (!(this.tutorialMode && this.tutorialSettings.noAvatar)) {
      participantIcon = participant.append('g')
        .attr('transform', function(d, i) {
          let x
          if (d.detected) {
            x = labeler.lab[i].y+detectZeroPoint - that.y(d.value)
          } else {
            x = labeler.lab[i].y+that.participantFaceRadius + that.nonDetectWidth/2 + that.participantSmallCircleRadius/2 + 18
          }
          if (that.expanded) {
            x = x + expandedOffset
          }
          let y = -that.labelOffset
          return translate(x, y)
        });


      if(this.useParticipantAvatar) {
        if (this.expanded || this.avatar == 'face-7') {
          participant.append('circle')
            .attr('x', -this.participantSmallCircleRadius)
            .attr('y', -this.participantSmallCircleRadius)
            .attr('r', this.participantSmallCircleRadius)
            .style('fill', colors.orange)
            .style('stroke', 'white')
            .style('stroke-width', 2)
        } else {
          participant.append('image')
              .attr('xlink:href', `${process.env.PUBLIC_URL}/images/${this.avatar}.png`)
              .attr('x', -this.participantFaceRadius)
              .attr('y', function(d) {
                if (!d.detected && !benchmarkDetected) {
                  return -that.participantFaceRadius-8
                } else {
                  return -that.participantFaceRadius
                }
              })
              .attr('width', this.participantFaceRadius*2)
              .attr('height', this.participantFaceRadius*2);

          participant.append('circle')
            // .attr('x', -this.participantFaceRadius)
            .attr('transform', function(d) {
              if (!d.detected && !benchmarkDetected) {
                return translate(0,-that.participantFaceRadius+2)
              } else {
                return translate(0,0)
              }
            })
            .attr('r', this.participantFaceRadius)
            .style('stroke', colors.orange)
            .style('stroke-width', 1)
            .style('fill', 'transparent');
        }

        if (this.expanded) {
          participantIcon.append('image')
            .attr('xlink:href', `${process.env.PUBLIC_URL}/images/${this.avatar}.png`)
            .attr('x', -this.faceSize / 2)
            .attr('y', -this.faceSize / 2)
            .attr('width', this.faceSize)
            .attr('height', this.faceSize);
        }
      }
      else {
        participant.append('circle')
          .attr('x', -this.participantFaceRadius)
          .attr('y', -this.participantFaceRadius)
          .attr('r', this.participantFaceRadius)
          .style('fill', colors.orange);

        participantIcon.append('circle')
            .attr('x', -(this.participantFaceRadius-2))
            .attr('y', -(this.participantFaceRadius-2))
            .attr('r', 2*(this.participantFaceRadius-2))
            .style('stroke-width', 2)
            .style('stroke', 'white')
            .style('fill', 'rgb(255, 80, 0)');
      }
    }

    //add actual value for benchmark
    let flagWidth = 20;
    let flagHeight = 14
    let self = this;

    let benchmarkIcon
    if (!(this.tutorialMode && this.tutorialSettings.noBenchmark)) {
      benchmarkIcon = this.chart.selectAll('.benchmark-icon')
        .data(benchmarks)
        .enter()
        .append('g')
        .attr('transform', function(d) {
          if (d.detected) {
            return translate(self.y(d.value)-flagWidth/2, self.graphMidline-benchmarkHeight)
          } else {
            if (self.expanded) {
            return translate(self.nonDetectCenter - 13, self.graphMidline+15)
            } else {
              if (participantDetected) {
                return translate(self.nonDetectCenter - 13, self.graphMidline-18)
              } else {
                return translate(self.nonDetectCenter - 13, self.graphMidline+7)
              }
            }
          }
        })

      benchmarkIcon.append('image')
        .attr('xlink:href', (d) => {
          if(d.symbol === 'star')
            return '';
          else
            {
              return `${process.env.PUBLIC_URL}/images/usflag.svg`
            }
          })
        .attr('transform', translate(0,-4))
        .attr('width', 20)
        .attr('height', 20);

      benchmarkIcon.append('rect')
        .attr("x", 0)
        .attr("y", -1)
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("width", flagWidth)
        .attr("height", flagHeight)
        .style('fill', 'none')
        .style('stroke-width', 1.5)
        .style('stroke', "rgb(13 94 143)");


        benchmarkIcon.on('click', function() {
          if(that.onBenchmarkClicked) {
            that.onBenchmarkClicked();
          }
        });


      if (this.expanded) {
        // get the width of the text
        let textData = ['a', 'b', 'c']    // your text here
        let textWidth = []

        this.chart.append('g')
            .selectAll('.dummyText')     // declare a new CSS class 'dummyText'
            .data(Array.from(benchmarks, x => valuetext(x)))
            .enter()                     // create new element
            .append("text")              // add element to class
            .attr('font-size', '14px')
            .attr('font-weight', 'bold')
            .attr("opacity", 0.0)      // not really necessary
            .text(function(d) { return d})
            .each(function(d,i) {
                var thisWidth = this.getComputedTextLength()
                textWidth.push(thisWidth)
                this.remove() // remove them just after displaying them
            })
        let benchmarkTextWidth = textWidth[0]+6


        let benchmark_text = this.chart.selectAll('.benchmark-text')
          .data(benchmarks)
          .enter()
          .append('g')
          .attr('transform', function(d) {
            if (d.detected) {
              let middleXValue = self.y(d.value)
              if (middleXValue - benchmarkTextWidth/2 < self.nonDetectWidth+self.margin.left) {
                middleXValue = self.nonDetectWidth+self.margin.left + benchmarkTextWidth/2
              }
              return translate(middleXValue, self.graphMidline-benchmarkHeight)
            } else {
              return translate(self.nonDetectCenter - 3, self.graphMidline+52)
            }
          })
          // .attr('transform', (d) => translate(d.detected ? this.y(d.value) : that.nonDetectCenter - 3, this.graphMidline-benchmarkHeight));

        benchmark_text.append('rect')
          .attr('x', -benchmarkTextWidth/2)
          .attr('width', benchmarkTextWidth)
          .attr('y', -24)
          .attr('height', 18)
          .attr("rx", 8)
          .attr("ry", 8)
          .style('fill', function(d) {
            if (d.detected) {return 'rgba(255,255,255,0.75)'}
            else {return "none"}
          })

        benchmark_text.append('text')
          .attr('y',-flagHeight)
          // .attr('width',0)
          .attr('text-anchor', 'middle')
          .attr('alignment-baseline', 'middle')
          .attr('fill', colors.blue)
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .text((d) => valuetext(d));


        benchmark_text.on('click', function() {
          if(that.onBenchmarkClicked) {
            that.onBenchmarkClicked();
          }
        });
      }
    }

    function valuetext(d) {
      let thevaluetext = `${d.value} ${that.units}`
      if (!d.detected) thevaluetext = that.noDetectText;
      return thevaluetext;
    }

    if (!(this.tutorialMode && this.tutorialSettings.noBenchmark)) {
      if (this.expanded) {
        participantIcon.append('circle')
          .attr('r', this.faceSize / 2)
          .style('fill', this.useParticipantAvatar ? 'transparent' : 'white')
          .style('stroke', colors.orange)
          .style('stroke-width', 2);
      }

      //add actual value
      if (this.expanded) {
        participantIcon.append('text')
          .attr('x',0)
          .attr('y', -this.faceSize + 14)
          .attr('width',0)
          .attr('text-anchor', 'middle')
          // .attr('alignment-baseline', 'top')
          .attr('fill', colors.orange)
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .text((d) => valuetext(d));
      }

      // if(this.useParticipantAvatar === false) {
        // participantIcon.append('text')
        //   .text('you')
        //   .attr('text-anchor', 'middle')
        //   .attr('alignment-baseline', 'central');
      // }

      if(this.showVisitBadges) {
        const visitBadge = participantIcon.append('g')
          .attr('transform', `translate(${this.faceSize / 2 - 5}, -${this.faceSize / 2 - 5})`);

        visitBadge.append('circle')
          .attr('r', (d) => !isNaN(d.visit) ? 15 : 0)
          .attr('stroke', colors.white)
          .attr('stroke-width', 2)
          .style('fill', colors.orange);

        visitBadge.append('text')
          .attr('text-anchor', 'middle')
          .attr('alignment-baseline', 'middle')
          .attr('fill', colors.white)
          .text((d) => !isNaN(d.visit) ? d.visit : "");
      }

      participantIcon.on('click', function(d) {
        if(that.onParticipantClicked) {
          that.onParticipantClicked(d);
        }
      });
    }

    // if a tutorial, draw in sample participants A, B, and C
    if (this.tutorialMode && this.tutorialSettings.drawSampleDots) {
      let sampleRadius = 10;

      // here2
      // dots should be at:
      // A: nondetect
      // B: 40th percentile, or if 20th percentile is nondetect, then the smallest detect
      // C: 98th percetile, or if it would overlap with B, then B + 2.5 facewidths
      let a_x = self.nonDetectCenter - sampleRadius*2;

      let b_participant;
      for (let i = 0; i < this.data.length; i++) {
        let person = this.data[i]
        if (person.detected && person.percentile > 0.2) {
          // first detected person above the 40th percentile
          b_participant = person;
          break;
        }
      }
      let b_x = this.y(b_participant.value);

      let b_plus_facewidth = b_x + this.participantFaceRadius*5;
      let c_participant;
      for (let i = 0; i < this.data.length; i++) {
        let person = this.data[i]
        if (person.detected && person.percentile > 0.98) {
          // first detected person above the 85th percentile
          c_participant = person;
          break;
        }
      }
      let c_participant_x = this.y(c_participant.value)
      let c_x = Math.max(b_plus_facewidth, c_participant_x)


      let sampleDots = this.chart.selectAll('.sample-dots')
        .data(["A", "B", "C"])
        .enter()
        .append('g')
        .attr('transform', translate(0, self.graphMidline))

      sampleDots.append("circle")
        .attr("cx", function(d, i) {
          if (d == "A") {
            return a_x
          } else if (d == "B") {
            return b_x
          }
          return c_x
        })
        .attr("cy", 0)
        .attr("r", sampleRadius)
        .style("fill", "#c51149")
        .style("stroke", "none")

      sampleDots.append('text')
        .attr("x", function(d, i) {
          if (d == "A") {
            return a_x
          } else if (d == "B") {
            return b_x
          }
          return c_x
        })
        .attr("y", 1)
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('fill', colors.white)
        .attr('font-weight', 'bold')
        .style('font-size', '14px')
        .text((d) => d)


    }


  } // end updateData()

  map(old_value, old_bottom, old_top, new_bottom, new_top) {
    let new_value = (old_value - old_bottom) / (old_top - old_bottom) * (new_top - new_bottom) + new_bottom
    return new_value;
  }

} // end class-SinaPlot

export default SinaPlot;



// WEBPACK FOOTER //
// ./src/SinaPlot.js